// =============================================================================
// SIREPPAR - Página de Verificación de Constancias (Pública)
// =============================================================================

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { TIPO_TRAMITE_LABELS } from '../../utils/tiposExpediente';

interface ConstanciaVerificada {
  valido: boolean;
  expediente?: {
    numero: string;
    tipo: string;
    estado: string;
    partido: string;
    fechaPresentacion?: string;
    fechaResolucion?: string;
  };
}

const estadoLabels: Record<string, { label: string; color: string }> = {
  BORRADOR: { label: 'Borrador', color: 'bg-gray-100 text-gray-800' },
  PRESENTADO: { label: 'Presentado', color: 'bg-blue-100 text-blue-800' },
  EN_ANALISIS: { label: 'En Revisión', color: 'bg-yellow-100 text-yellow-800' },
  OBSERVADO: { label: 'Observado', color: 'bg-orange-100 text-orange-800' },
  APROBADO: { label: 'Aprobado', color: 'bg-green-100 text-green-800' },
  RECHAZADO: { label: 'Rechazado', color: 'bg-red-100 text-red-800' },
  ARCHIVADO: { label: 'Archivado', color: 'bg-gray-100 text-gray-600' },
};

const tipoLabels: Record<string, string> = {
  ...TIPO_TRAMITE_LABELS,
  RECONOCIMIENTO: 'Reconocimiento de Personería',
};

export default function VerificarConstanciaPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState<ConstanciaVerificada | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (codigo) {
      verificarConstancia(codigo);
    }
  }, [codigo]);

  const verificarConstancia = async (codigoVerificacion: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/expedientes/verificar/${codigoVerificacion}`);
      setResultado(response.data.data);
    } catch (err: any) {
      setError('No se pudo verificar la constancia. El código puede ser inválido o el expediente no existe.');
      setResultado({ valido: false });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-primary-700 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🏛️</span>
            </div>
            <div>
              <h1 className="text-xl font-bold">SIREPPAR</h1>
              <p className="text-primary-200 text-sm">Sistema de Registro de Partidos Políticos</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-primary-50 px-6 py-4 border-b">
            <h2 className="text-xl font-semibold text-primary-900">Verificación de Constancia</h2>
            <p className="text-primary-600 text-sm">Código: {codigo}</p>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Verificando constancia...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">❌</span>
                </div>
                <h3 className="text-xl font-semibold text-red-800 mb-2">Constancia No Válida</h3>
                <p className="text-gray-600">{error}</p>
              </div>
            ) : resultado?.valido && resultado.expediente ? (
              <div>
                {/* Validación exitosa */}
                <div className="text-center mb-8">
                  <div className="mx-auto h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-4xl">✅</span>
                  </div>
                  <h3 className="text-2xl font-bold text-green-800">Constancia Válida</h3>
                  <p className="text-gray-600">Este documento es auténtico y fue emitido por la Secretaría Electoral</p>
                </div>

                {/* Datos del expediente */}
                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <h4 className="font-semibold text-gray-900 mb-4">Datos del Expediente</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Número de Expediente</span>
                      <p className="font-medium text-lg">{resultado.expediente.numero}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Estado</span>
                      <p>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          estadoLabels[resultado.expediente.estado]?.color || 'bg-gray-100'
                        }`}>
                          {estadoLabels[resultado.expediente.estado]?.label || resultado.expediente.estado}
                        </span>
                      </p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-500">Tipo de Trámite</span>
                      <p className="font-medium">{tipoLabels[resultado.expediente.tipo] || resultado.expediente.tipo}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-500">Partido Político</span>
                      <p className="font-medium">{resultado.expediente.partido}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Fecha de Presentación</span>
                      <p className="font-medium">{formatDate(resultado.expediente.fechaPresentacion)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Fecha de Resolución</span>
                      <p className="font-medium">{formatDate(resultado.expediente.fechaResolucion)}</p>
                    </div>
                  </div>
                </div>

                {/* Sello de verificación */}
                <div className="mt-6 border-2 border-dashed border-green-300 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🔐</span>
                    <div>
                      <p className="font-medium text-green-800">Documento verificado digitalmente</p>
                      <p className="text-sm text-green-600">
                        Verificación realizada el {new Date().toLocaleString('es-AR')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h3 className="text-xl font-semibold text-red-800 mb-2">Constancia No Encontrada</h3>
                <p className="text-gray-600">El código de verificación no corresponde a ningún expediente registrado.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Secretaría Electoral Permanente - Provincia del Chubut</p>
          <p className="mt-1">
            <Link to="/login" className="text-primary-600 hover:underline">
              Acceder al sistema
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
